import type React from 'react'
import { useRef } from 'react'
import type { PortalProps } from '../Portal/Portal.types'
import { useEventListener } from './EventListener'

export interface ClickOutsideConfig extends Pick<PortalProps, 'container'> {
  /**
   * @since 10.19.0
   */
  onClickOutside: (event: MouseEvent) => void
  /**
   * @since 10.19.0
   */
  refs: Array<React.RefObject<HTMLElement>>
}

export interface ClickOutsideProps extends ClickOutsideConfig {
  /**
   * @since 10.19.0
   */
  children: React.ReactElement
}

/**
 * @deprecatedSince 9
 * @deprecated Never officially documented/supported
 */
// To support MenuRef had to change to any. Could manually do <HTMLElement & MenuRef> but seems brittle
export function isEventSource(ref: React.RefObject<any>, event: Event) {
  if (!ref?.current || !event) {
    return false
  }

  if (
    event.target instanceof HTMLElement ||
    event.target instanceof SVGElement
  ) {
    if (typeof event.composedPath === 'function') {
      return event.composedPath().includes(ref.current)
    }

    return ref.current.contains(event.target)
  }

  return false
}

/**
 * @deprecatedSince 9
 * @deprecated Never officially documented/supported
 */
export function useClickOutside({
  refs = [],
  onClickOutside,
  container,
}: ClickOutsideConfig) {
  const mousedownTarget = useRef<EventTarget | null>(null)

  const handleMouseDown = (event: MouseEvent) => {
    mousedownTarget.current = event.target
  }

  // Only trigger if the mouseup target is the same as the mousedown target
  // and it's outside of our refs
  const handleMouseUp = (event: MouseEvent) => {
    const isSameTarget =
      mousedownTarget.current instanceof Element &&
      event.target instanceof Element &&
      mousedownTarget.current.isSameNode?.(event.target)
    const isOutsideTarget = !refs.find((ref) => isEventSource(ref, event))
    if (isOutsideTarget && isSameTarget) {
      onClickOutside(event)
    }
    mousedownTarget.current = null
  }

  const options = { capture: true }
  const containerDocument = container?.ownerDocument
  const scope = containerDocument?.defaultView || undefined

  useEventListener({
    event: 'mousedown',
    handler: handleMouseDown,
    options,
    scope: scope,
  })

  useEventListener({
    event: 'mouseup',
    handler: handleMouseUp,
    options,
    scope: scope,
  })

  useEventListener({
    event: 'touchend',
    handler: handleMouseUp,
    options,
    scope: scope,
  })
}
